import { Tag } from 'antd'
import dayjs from 'dayjs'
import {
    MdOutlineAddTask,
    MdOutlineAlarm,
    MdOutlineAttachFile,
    MdOutlineChatBubbleOutline,
    MdOutlineCheckCircle,
    MdOutlineDelete,
    MdOutlineEdit,
    MdOutlineEventRepeat,
    MdOutlineFlag,
    MdOutlinePause,
    MdOutlinePersonAddAlt,
    MdOutlinePlayArrow,
    MdOutlineRestoreFromTrash,
    MdOutlineSwapHoriz,
} from 'react-icons/md'

import {
    HistoryDiff,
    HistoryField,
    HistoryFields,
    HistoryFile,
    HistoryInline,
    HistoryMuted,
    HistoryQuote,
} from '@/Ship/Components/History'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { formatDateUk, UserChip } from '@/Ship/Helpers/fieldViews'

import { HISTORY_EVENT_MAP } from '../Contracts/TaskHistoryEvent'
import { STATUS_MAP } from '../Contracts/TaskStatus'
import { formatMinutes } from './taskFieldFormat'

import type { HistoryEntry, HistoryTone } from '@/Ship/Components/History'
import type { ReactNode } from 'react'
import type { TaskHistoryItem, TaskUserRef } from '../Contracts/Task.types'
import type { TaskHistoryEventCode } from '../Contracts/TaskHistoryEvent'
import type { TaskStatus } from '../Contracts/TaskStatus'

/** Що стрічці треба знати поза самими подіями. */
export interface TaskHistoryContext {
    /** Люди, згадані в змінах, за uuid */
    users: ReadonlyMap<string, TaskUserRef>
    /** Тексти коментарів за uuid */
    comments: ReadonlyMap<string, string>
    commentsLoaded: boolean
    /** Файли завдання: uuid → назва */
    files: ReadonlyMap<string, string>
    /** Батьківське завдання — його показує подія створення */
    parent?: { uuid?: string | undefined; number?: number | undefined; title?: string | undefined } | null | undefined
}

type Payload = Record<string, unknown>

const asList = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

const statusTag = (value: unknown) => {
    const info = typeof value === 'string' ? STATUS_MAP[value as TaskStatus] : undefined
    return <StatusTag label={info?.label ?? String(value ?? '—')} color={info?.color} inline />
}

const priorityTag = (
    <Tag color="error" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginInlineEnd: 0 }}>
        <MdOutlineFlag />
        Пріоритетне
    </Tag>
)

/** Перший абзац коментаря — стрічка не місце для всього тексту. */
const excerpt = (text: string, max = 220): string => (text.length > max ? `${text.slice(0, max).trimEnd()}…` : text)

/** Табличка змін або нічого — порожня рамка гірша за її відсутність. */
const fieldsOrNull = (rows: ReactNode[]): ReactNode => (rows.length > 0 ? <HistoryFields>{rows}</HistoryFields> : null)

interface Heading {
    title: string
    icon: ReactNode
    tone: HistoryTone
}

/**
 * Зміна статусу — не «Змінено статус» для всіх, а що саме сталося: «Взято в
 * роботу», «Роботу призупинено». Заголовок читають першим, бейджі під ним
 * лише уточнюють.
 */
const statusHeading = (from: unknown, to: unknown): Heading => {
    switch (to) {
        case 'in_process':
            if (from === 'suspended') return { title: 'Роботу відновлено', icon: <MdOutlinePlayArrow />, tone: 'blue' }
            if (from === 'completed') return { title: 'Завдання відкрито знову', icon: <MdOutlinePlayArrow />, tone: 'blue' }
            return { title: 'Взято в роботу', icon: <MdOutlinePlayArrow />, tone: 'blue' }
        case 'suspended':
            return { title: 'Роботу призупинено', icon: <MdOutlinePause />, tone: 'gold' }
        case 'completed':
            return { title: 'Завдання завершено', icon: <MdOutlineCheckCircle />, tone: 'green' }
        case 'overdue':
            return { title: 'Минув дедлайн', icon: <MdOutlineAlarm />, tone: 'red' }
        default:
            return { title: 'Змінено статус', icon: <MdOutlineSwapHoriz />, tone: 'purple' }
    }
}

const ICONS: Record<TaskHistoryEventCode, ReactNode> = {
    created: <MdOutlineAddTask />,
    updated: <MdOutlineEdit />,
    deadline_changed: <MdOutlineEventRepeat />,
    assigned: <MdOutlinePersonAddAlt />,
    status_changed: <MdOutlineSwapHoriz />,
    removed: <MdOutlineDelete />,
    restored: <MdOutlineRestoreFromTrash />,
    comment_added: <MdOutlineChatBubbleOutline />,
    file_attached: <MdOutlineAttachFile />,
}

/**
 * Будує рядки стрічки з подій завдання (новіші зверху, як віддав бек).
 *
 * Подробиці — табличкою «поле │ що зроблено». Де зміну видно як «було →
 * стало» (назва, дедлайн, керівник, статус), стоїть стрілка; де так не
 * показати (опис, склад людей, файли), — мітка дії: «змінено», «додано»,
 * «знято».
 */
export const buildTaskHistoryEntries = (items: TaskHistoryItem[], ctx: TaskHistoryContext): HistoryEntry[] => {
    const person = (uuid: unknown): ReactNode => {
        const user = typeof uuid === 'string' ? ctx.users.get(uuid) : undefined
        return user ? <UserChip user={user} /> : <HistoryMuted>невідомий користувач</HistoryMuted>
    }

    const people = (uuids: string[]): ReactNode => (
        <HistoryInline>
            {uuids.map((uuid) => (
                <span key={uuid}>{person(uuid)}</span>
            ))}
        </HistoryInline>
    )

    /** Рядки «додано / знято» для набору людей. */
    const peopleChanges = (label: string, before: string[], after: string[], addedHint: string[] = []): ReactNode[] => {
        const added = addedHint.length > 0 ? addedHint : after.filter((uuid) => !before.includes(uuid))
        const removed = before.filter((uuid) => !after.includes(uuid))
        const rows: ReactNode[] = []

        if (added.length > 0) {
            rows.push(
                <HistoryField key={`${label}-added`} label={label} action="added">
                    {people(added)}
                </HistoryField>
            )
        }
        if (removed.length > 0) {
            rows.push(
                <HistoryField key={`${label}-removed`} label={label} action="removed">
                    {people(removed)}
                </HistoryField>
            )
        }

        return rows
    }

    const describe = (item: TaskHistoryItem): { heading: Heading; details: ReactNode } => {
        const code = item.event
        const info = HISTORY_EVENT_MAP[code] ?? { label: String(code), tone: 'neutral' as const }
        const base: Heading = { title: info.label, icon: ICONS[code] ?? <MdOutlineEdit />, tone: info.tone }
        const before: Payload = item.payload?.old ?? {}
        const after: Payload = item.payload?.new ?? {}

        switch (code) {
            case 'created': {
                // Значення на момент створення — без міток дій: це не зміна
                const rows: ReactNode[] = []

                if (typeof after['deadline'] === 'string') {
                    rows.push(
                        <HistoryField key="deadline" label="Дедлайн">
                            {formatDateUk(after['deadline'])}
                        </HistoryField>
                    )
                }
                if (typeof after['manager'] === 'string') {
                    rows.push(
                        <HistoryField key="manager" label="Керівник">
                            {person(after['manager'])}
                        </HistoryField>
                    )
                }
                const supervisors = asList(after['supervisors'])
                if (supervisors.length > 0) {
                    rows.push(
                        <HistoryField key="supervisors" label="Спостерігачі">
                            {people(supervisors)}
                        </HistoryField>
                    )
                }
                if (typeof after['parent'] === 'string') {
                    const parent = ctx.parent?.uuid === after['parent'] ? ctx.parent : undefined
                    rows.push(
                        <HistoryField key="parent" label="Підзавдання до">
                            {parent ? `№ ${parent.number ?? ''} ${parent.title ?? ''}`.trim() : 'іншого завдання'}
                        </HistoryField>
                    )
                }
                if (after['isPriority'] === true) {
                    rows.push(
                        <HistoryField key="priority" label="Пріоритет">
                            {priorityTag}
                        </HistoryField>
                    )
                }

                return { heading: base, details: fieldsOrNull(rows) }
            }

            case 'assigned':
                return {
                    heading: base,
                    details: fieldsOrNull(
                        peopleChanges(
                            'Виконавці',
                            asList(before['responsibles']),
                            asList(after['responsibles']),
                            asList((item.payload as { added?: unknown } | undefined)?.added)
                        )
                    ),
                }

            case 'deadline_changed': {
                const from = before['deadline']
                const to = after['deadline']
                const shift =
                    typeof from === 'string' && typeof to === 'string'
                        ? dayjs(to).startOf('day').diff(dayjs(from).startOf('day'), 'day')
                        : 0

                return {
                    heading: base,
                    details: fieldsOrNull([
                        <HistoryField key="deadline" label="Дедлайн">
                            <HistoryDiff
                                from={formatDateUk(from)}
                                to={formatDateUk(to)}
                                suffix={shift !== 0 ? `${shift > 0 ? '+' : '−'}${Math.abs(shift)} дн.` : undefined}
                            />
                        </HistoryField>,
                    ]),
                }
            }

            case 'status_changed': {
                const minutes = after['timeSpentInMinutes']
                const rows: ReactNode[] = [
                    <HistoryField key="status" label="Статус">
                        <HistoryDiff from={statusTag(before['status'])} to={statusTag(after['status'])} strike={false} />
                    </HistoryField>,
                ]

                if (typeof minutes === 'number' && minutes > 0) {
                    rows.push(
                        <HistoryField key="time" label="Витрачено часу">
                            {formatMinutes(minutes)}
                        </HistoryField>
                    )
                }

                return { heading: statusHeading(before['status'], after['status']), details: fieldsOrNull(rows) }
            }

            case 'updated': {
                const rows: ReactNode[] = []

                if ('title' in after) {
                    rows.push(
                        <HistoryField key="title" label="Назва">
                            <HistoryDiff from={`«${String(before['title'] ?? '')}»`} to={`«${String(after['title'] ?? '')}»`} />
                        </HistoryField>
                    )
                }
                if ('description' in after) {
                    rows.push(<HistoryField key="description" label="Опис" action="changed" />)
                }
                if ('isPriority' in after) {
                    rows.push(
                        <HistoryField key="isPriority" label="Пріоритет" action={after['isPriority'] === true ? 'added' : 'removed'}>
                            {priorityTag}
                        </HistoryField>
                    )
                }
                if ('manager' in after) {
                    rows.push(
                        <HistoryField key="manager" label="Керівник">
                            <HistoryDiff from={person(before['manager'])} to={person(after['manager'])} strike={false} />
                        </HistoryField>
                    )
                }
                if ('supervisors' in after) {
                    rows.push(...peopleChanges('Спостерігачі', asList(before['supervisors']), asList(after['supervisors'])))
                }
                if ('parent' in after) {
                    rows.push(<HistoryField key="parent" label="Батьківське завдання" action="changed" />)
                }

                return { heading: base, details: fieldsOrNull(rows) }
            }

            case 'comment_added': {
                const uuid = after['comment']
                const text = typeof uuid === 'string' ? ctx.comments.get(uuid) : undefined

                return {
                    heading: base,
                    details:
                        text !== undefined ? (
                            <HistoryQuote>{excerpt(text)}</HistoryQuote>
                        ) : ctx.commentsLoaded ? (
                            <HistoryMuted>коментар видалено</HistoryMuted>
                        ) : null,
                }
            }

            case 'file_attached': {
                const uuids = asList(after['files'])

                return {
                    heading: { ...base, title: uuids.length > 1 ? 'Прикріплено файли' : 'Прикріплено файл' },
                    details: fieldsOrNull(
                        uuids.length > 0
                            ? [
                                  <HistoryField key="files" label="Файли" action="added">
                                      <HistoryInline>
                                          {uuids.map((uuid) => (
                                              <HistoryFile
                                                  key={uuid}
                                                  name={ctx.files.get(uuid) ?? <HistoryMuted>файл видалено</HistoryMuted>}
                                              />
                                          ))}
                                      </HistoryInline>
                                  </HistoryField>,
                              ]
                            : []
                    ),
                }
            }

            default:
                return { heading: base, details: null }
        }
    }

    return items.map((item, index) => {
        const { heading, details } = describe(item)

        return {
            key: `${item.occurredAt ?? ''}-${index}`,
            title: heading.title,
            icon: heading.icon,
            tone: heading.tone,
            // Порожній автор — подія системи (планувальник простроченого)
            actor: item.changedBy ?? null,
            occurredAt: item.occurredAt,
            details,
        }
    })
}
